/**
 * MiningHistory — "¿cuánto rendí en el período? ¿qué ore rinde más?"
 *
 *  - Period toggle (7d / 30d / current_month).
 *  - Summary tiles: total ISK @ Jita buy, días activos, mejor día, top ore.
 *  - Inline SVG daily chart (bars per day). Sin libs, sin canvas.
 *  - By-ore breakdown table (ranked, share %, ISK/unit).
 *  - Caveats: Jita buy = piso realista; ESI mining = últimos ~30d;
 *    top-N cap en types (orphaned qty queda reportada como "skipped").
 */

import { useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { CHART_COLORS } from '../lib/chartColors'

type Period = 'current_month' | '7d' | '30d'

type OreAgg = {
  type_id: number
  name: string
  quantity: number
  value: number
  unit_price: number | null
  share_pct: number
}

type DayAgg = {
  date: string
  quantity: number
  value: number
  ores: Array<{
    type_id: number
    name: string
    quantity: number
    line_value: number
    unit_price: number | null
  }>
}

export function MiningHistory() {
  const [period, setPeriod] = useState<Period>('current_month')
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const days = period === '7d' ? 7 : period === '30d' ? 30 : undefined
  const currentMonth = period === 'current_month'

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['mining-history', forceRefetchKey, period],
    queryFn: () =>
      api.miningHistory({ days, currentMonth }, force),
  })

  const s = data?.summary
  const byDay: DayAgg[] = data?.by_day ?? []
  const byOre: OreAgg[] = data?.by_ore ?? []

  return (
    <div className="card">
      <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
        <div>
          <div className="stat-label">Mining history</div>
          <div className="text-eve-muted text-xs">
            Lo que minaste, valuado a Jita buy: total por período, por día y
            por ore. Cero input.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <PeriodToggle value={period} onChange={setPeriod} />
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      {/* Summary tiles */}
      {s && s.total_value > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <SummaryTile
            label="Total rendido"
            value={`${formatISKShort(s.total_value)} ISK`}
            sub={`${s.total_quantity.toLocaleString('en-US')} u · ${s.days_active}/${s.total_days_in_period} días`}
            accent
          />
          <SummaryTile
            label="Promedio / día activo"
            value={`${formatISKShort(s.avg_value_per_active_day)} ISK`}
            sub={
              s.avg_value_per_active_day > 0 && s.avg_value_per_day > 0
                ? `${formatISKShort(s.avg_value_per_day)}/día calendario`
                : undefined
            }
          />
          <SummaryTile
            label="Mejor día"
            value={
              s.top_day
                ? `${formatISKShort(s.top_day.value)} ISK`
                : '—'
            }
            sub={s.top_day ? s.top_day.date : '—'}
            positive={!!s.top_day}
          />
          <SummaryTile
            label="Top ore"
            value={s.top_ore ? s.top_ore.name : '—'}
            sub={
              s.top_ore
                ? `${formatISKShort(s.top_ore.value)} ISK · ${s.top_ore.quantity.toLocaleString('en-US')} u`
                : '—'
            }
            positive={!!s.top_ore}
          />
        </div>
      )}

      {/* Daily chart */}
      {byDay.length > 0 && (
        <div className="mb-4">
          <div className="stat-label mb-2">Por día</div>
          <DailyChart days={byDay} />
        </div>
      )}

      {/* By ore */}
      {byOre.length > 0 && (
        <div className="mb-2">
          <div className="stat-label mb-2">Por ore</div>
          <ByOreTable ores={byOre} />
        </div>
      )}

      {/* States */}
      {isLoading ? (
        <MiningHistorySkeleton />
      ) : s?.total_value === 0 && s?.total_quantity === 0 ? (
        <div className="text-eve-muted py-8 text-center italic">
          Sin actividad de mining en este período.
        </div>
      ) : null}

      {s && s.skipped_types > 0 && (
        <div className="text-[11px] text-eve-warn mt-2 italic">
          {s.skipped_types} tipo{s.skipped_types === 1 ? '' : 's'} omitido
          {s.skipped_types === 1 ? '' : 's'} del cálculo (top 30 por qty). El
          total puede estar subestimado.
        </div>
      )}

      <p className="text-xs text-eve-muted mt-3">
        Precios a Jita <strong>buy</strong> orders (piso realista para
        vendedores). ESI mining ledger = últimos ~30 días; períodos más
        largos devolverán vacío.
      </p>
    </div>
  )
}

// ============================================================================
// Sub-components
// ============================================================================

function PeriodToggle({
  value,
  onChange,
}: {
  value: Period
  onChange: (p: Period) => void
}) {
  const options: Array<{ key: Period; label: string }> = [
    { key: '7d', label: '7d' },
    { key: 'current_month', label: 'Month' },
    { key: '30d', label: '30d' },
  ]
  return (
    <div className="flex gap-1">
      {options.map((o) => (
        <button
          key={o.key}
          onClick={() => onChange(o.key)}
          className={`text-[11px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
            value === o.key
              ? 'bg-eve-accent text-eve-bg border-eve-accent font-semibold'
              : 'bg-eve-panel border-eve-border text-eve-muted hover:text-eve-text'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function SummaryTile({
  label,
  value,
  sub,
  accent,
  positive,
}: {
  label: string
  value: string
  sub?: string
  accent?: boolean
  positive?: boolean
}) {
  const valueClass = positive
    ? 'text-eve-success'
    : accent
      ? 'text-eve-accent'
      : 'text-eve-text'
  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3">
      <div className="stat-label">{label}</div>
      <div className={`font-mono text-lg font-semibold mt-1 ${valueClass}`}>
        {value}
      </div>
      {sub && (
        <div className="text-[10px] text-eve-muted mt-1 italic truncate">
          {sub}
        </div>
      )}
    </div>
  )
}

/**
 * Daily chart — inline SVG, 1 bar por día, altura proporcional al value.
 * Gap intentional entre días vacíos (rellenamos con height=0).
 * Eje X = fechas (3-5 labels). Eje Y = implícito, mostramos en tooltip
 * al hover de cada barra.
 *
 * Hover: crosshair vertical + HTML tooltip overlay con fecha / total / qty /
 * top ore. Reemplaza el SVG <title> (browser tooltips son lentos y feos).
 */
function DailyChart({ days }: { days: DayAgg[] }) {
  const svgRef = useRef<SVGSVGElement>(null)
  const [hoverIdx, setHoverIdx] = useState<number | null>(null)

  const maxValue = useMemo(
    () => Math.max(...days.map((d) => d.value), 1),
    [days],
  )

  // Lay out: SVG de 100% width, alto fijo. Padding left para eje Y.
  const W = 800
  const H = 140
  const PAD_LEFT = 0
  const PAD_RIGHT = 8
  const PAD_TOP = 12
  const PAD_BOTTOM = 28

  const innerW = W - PAD_LEFT - PAD_RIGHT
  const innerH = H - PAD_TOP - PAD_BOTTOM

  const N = days.length
  const slotW = N > 0 ? innerW / N : 0
  const barW = Math.max(2, Math.min(20, slotW * 0.7))

  // Pick date labels to avoid crowding: ~5 evenly spaced
  const labelCount = Math.min(5, N)
  const labelEvery = N > 0 ? Math.max(1, Math.floor(N / labelCount)) : 1

  // Y ticks: top + mid
  const yTicks = [
    { value: maxValue, label: formatISKShort(maxValue) },
    { value: maxValue / 2, label: formatISKShort(maxValue / 2) },
  ]

  // Hover — encontrar index más cercano por X.
  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const svg = svgRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const xRatio = (e.clientX - rect.left) / rect.width
    const xInViewBox = xRatio * W
    let bestIdx = 0
    let bestDist = Infinity
    for (let i = 0; i < days.length; i++) {
      const cx = PAD_LEFT + slotW * i + slotW / 2
      const d = Math.abs(cx - xInViewBox)
      if (d < bestDist) {
        bestDist = d
        bestIdx = i
      }
    }
    setHoverIdx(bestIdx)
  }

  const hoverDay = hoverIdx !== null ? days[hoverIdx] : null
  const hoverX = hoverIdx !== null ? PAD_LEFT + slotW * hoverIdx + slotW / 2 : 0
  const hoverBarTop =
    hoverDay !== null
      ? PAD_TOP + innerH - Math.max(1, (hoverDay.value / maxValue) * innerH)
      : 0

  // Top ore del día (line_value), para el tooltip.
  const hoverTopOre =
    hoverDay && hoverDay.ores.length > 0
      ? [...hoverDay.ores].sort((a, b) => b.line_value - a.line_value)[0]
      : null

  return (
    <div className="bg-eve-bg border border-eve-border rounded-md p-3 overflow-hidden">
      <div className="relative">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          className="w-full h-32"
          role="img"
          aria-label="Mining revenue per day"
          onMouseMove={onMove}
          onMouseLeave={() => setHoverIdx(null)}
        >
          {/* Y grid lines */}
          {yTicks.map((t, i) => {
            const y = PAD_TOP + innerH * (1 - t.value / maxValue)
            return (
              <g key={i}>
                <line
                  x1={PAD_LEFT}
                  x2={W - PAD_RIGHT}
                  y1={y}
                  y2={y}
                  stroke="currentColor"
                  strokeOpacity={0.08}
                  strokeDasharray="2,3"
                />
                <text
                  x={W - PAD_RIGHT}
                  y={y - 2}
                  fontSize={9}
                  textAnchor="end"
                  fill="currentColor"
                  opacity={0.4}
                  className="font-mono"
                >
                  {t.label}
                </text>
              </g>
            )
          })}

          {/* Bars */}
          {days.map((d, i) => {
            const cx = PAD_LEFT + slotW * i + slotW / 2
            const h = (d.value / maxValue) * innerH
            const y = PAD_TOP + innerH - h
            const x = cx - barW / 2
            const labelIdx = Math.floor(i / labelEvery) * labelEvery
            const showLabel =
              labelIdx === i ||
              i === 0 ||
              i === N - 1 ||
              i === labelIdx
            const isHover = hoverIdx === i
            return (
              <g key={d.date}>
                <rect
                  x={x}
                  y={y}
                  width={barW}
                  height={Math.max(1, h)}
                  fill={isHover ? CHART_COLORS.accent2 : CHART_COLORS.accent}
                  fillOpacity={isHover ? 1 : 0.85}
                  rx={1}
                />
                {showLabel && (
                  <text
                    x={cx}
                    y={H - 10}
                    fontSize={9}
                    textAnchor="middle"
                    fill="currentColor"
                    opacity={0.55}
                    className="font-mono"
                  >
                    {d.date.slice(5)}
                  </text>
                )}
              </g>
            )
          })}

          {/* Hover crosshair (encima de las barras) */}
          {hoverIdx !== null && (
            <g pointerEvents="none">
              <line
                x1={hoverX}
                y1={PAD_TOP}
                x2={hoverX}
                y2={H - PAD_BOTTOM}
                stroke={CHART_COLORS.muted}
                strokeWidth={1}
                strokeOpacity={0.5}
                strokeDasharray="2 3"
              />
            </g>
          )}
        </svg>
        {/* HTML tooltip overlay — positioned by viewBox % via container. */}
        {hoverDay && (
          <div
            className="absolute pointer-events-none bg-eve-panel border border-eve-border rounded-md px-2 py-1 text-[10px] font-mono shadow-lg whitespace-nowrap"
            style={{
              left: `${(hoverX / W) * 100}%`,
              top: `${(hoverBarTop / H) * 100}%`,
              transform: 'translate(-50%, calc(-100% - 8px))',
            }}
          >
            <div className="text-eve-muted">{hoverDay.date}</div>
            <div className="text-eve-text font-semibold">
              {hoverDay.value === 0
                ? '0 ISK'
                : `${formatISK(hoverDay.value, 0)} ISK`}
            </div>
            <div className="text-eve-muted text-[9px] mt-0.5">
              {hoverDay.quantity.toLocaleString('en-US')} unidades minadas
            </div>
            {hoverTopOre && hoverTopOre.line_value > 0 && (
              <div className="text-eve-accent text-[9px] mt-0.5">
                top: {hoverTopOre.name}
              </div>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between text-[10px] text-eve-muted mt-2">
        <span>{days[0]?.date}</span>
        <span>
          {days.length} día{days.length === 1 ? '' : 's'} con mining ·{' '}
          {days.reduce((a, d) => a + d.value, 0) === 0
            ? '0 ISK'
            : `${formatISKShort(days.reduce((a, d) => a + d.value, 0))} ISK`}
        </span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
    </div>
  )
}

function ByOreTable({ ores }: { ores: OreAgg[] }) {
  const max = useMemo(
    () => Math.max(...ores.map((o) => o.value), 1),
    [ores],
  )

  return (
    <div className="space-y-1.5">
      {ores.map((o) => {
        const pct = (o.value / max) * 100
        const unitPrice = o.unit_price
        return (
          <div
            key={o.type_id}
            className="flex items-center gap-2 text-xs"
          >
            <div className="w-40 truncate font-medium" title={o.name}>
              {o.name}
            </div>
            <div className="flex-1 h-3 bg-eve-border/40 rounded overflow-hidden relative">
              <div
                className="h-full bg-eve-accent"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-28 text-right font-mono text-eve-text">
              {formatISKShort(o.value)} ISK
            </div>
            <div className="w-14 text-right font-mono text-eve-muted">
              {o.share_pct.toFixed(1)}%
            </div>
            <div className="w-20 text-right font-mono text-eve-muted text-[10px]">
              {unitPrice !== null
                ? `${formatISKShort(unitPrice)} u⁻¹`
                : '—'}
            </div>
          </div>
        )
      })}
    </div>
  )
}

/**
 * Skeleton de la página completa. Mimic los 4 summary tiles + el chart
 * card + las filas del by-ore breakdown. Evita el "Cargando..." plano
 * que daba sensación de página rota durante el cold-load.
 */
function MiningHistorySkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" aria-live="polite">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-eve-bg border border-eve-border rounded-md p-3"
          >
            <div className="h-3 w-2/3 bg-eve-border/40 rounded animate-pulse" />
            <div className="h-6 w-3/4 bg-eve-border/40 rounded animate-pulse mt-2" />
            <div className="h-2 w-1/2 bg-eve-border/30 rounded animate-pulse mt-2" />
          </div>
        ))}
      </div>
      <div className="bg-eve-bg border border-eve-border rounded-md p-3 h-32 flex items-end gap-1">
        {[0.4, 0.7, 0.3, 0.9, 0.5, 0.8, 0.6, 0.4].map((h, i) => (
          <div
            key={i}
            className="flex-1 bg-eve-border/40 rounded-t animate-pulse"
            style={{ height: `${h * 100}%` }}
          />
        ))}
      </div>
      <div className="space-y-2">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-40 h-3 bg-eve-border/40 rounded animate-pulse" />
            <div className="flex-1 h-3 bg-eve-border/30 rounded animate-pulse" />
            <div className="w-28 h-3 bg-eve-border/40 rounded animate-pulse" />
          </div>
        ))}
      </div>
    </div>
  )
}
